import { translateText } from "../../../helpers/LanguageHelper";
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SiteBreadcrumb from '../../components/layout/SiteBreadcrumb';
import Config from '../../../helpers/Config';
import { MakeApiCallAsync } from '../../../helpers/ApiHelpers';
import { GetDefaultCurrencySymbol, getLanguageCodeFromSession } from '../../../helpers/CommonHelper';
import { meetsMinimumOrderTotal, MINIMUM_ORDER_MESSAGE, MINIMUM_ORDER_GUIDANCE } from '../../../helpers/OrderValidationHelper';

const WHATSAPP_NUMBER = '916380413953';
const WHATSAPP_PRODUCT_IDS = [1065, 1066, 1064];
const WHATSAPP_VARIANT_NAMES = {
    'uthukuli cow signature combo': '500g Cow Butter + 200ml Cow Ghee'
};

const asArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string' || !value.trim()) return [];

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const productPrice = (product) =>
    Number(product.DiscountedPrice) > 0 ? Number(product.DiscountedPrice) : Number(product.Price) || 0;

const productVariantName = (product) =>
    WHATSAPP_VARIANT_NAMES[String(product.ProductName || '').trim().toLowerCase()] || '';

const WhatsAppOrder = () => {
    const [products, setProducts] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const currency = GetDefaultCurrencySymbol();
    const langCode = getLanguageCodeFromSession();

    useEffect(() => {
        let isMounted = true;

        const loadWhatsAppProducts = async () => {
            try {
                const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
                const response = await MakeApiCallAsync(Config.END_POINT_NAMES.GET_PRODUCTS_LIST_BY_IDS, null, {
                    requestParameters: {
                        ProductsIds: JSON.stringify(WHATSAPP_PRODUCT_IDS.map(ProductId => ({ ProductId }))),
                        recordValueJson: '[]'
                    }
                }, headers, 'POST', true);

                const catalog = response?.data?.data ? asArray(response.data.data) : [];
                const allowedProducts = catalog
                    .map(product => ({ ...product, ProductId: product.ProductId ?? product.ProductID }))
                    .filter(product => WHATSAPP_PRODUCT_IDS.includes(Number(product.ProductId)))
                    .sort((first, second) =>
                        WHATSAPP_PRODUCT_IDS.indexOf(Number(first.ProductId)) - WHATSAPP_PRODUCT_IDS.indexOf(Number(second.ProductId))
                    );

                if (isMounted) {
                    setProducts(allowedProducts);
                }
            } catch (requestError) {
                console.error(requestError);
                if (isMounted) setError('We could not load the WhatsApp order products. Please try again.');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadWhatsAppProducts();
        return () => { isMounted = false; };
    }, []);

    const selectedProducts = useMemo(() => products
        .filter(product => Number(quantities[product.ProductId]) > 0)
        .map(product => ({ ...product, quantity: Number(quantities[product.ProductId]) })), [products, quantities]);

    const orderTotal = selectedProducts.reduce((total, product) => total + productPrice(product) * product.quantity, 0);
    const minimumOrderMet = meetsMinimumOrderTotal(orderTotal);

    const changeQuantity = (productId, change) => {
        setQuantities(current => ({
            ...current,
            [productId]: Math.max(0, Math.min(99, Number(current[productId] || 0) + change))
        }));
    };

    const placeOrder = () => {
        if (!selectedProducts.length || !minimumOrderMet) return;

        const lines = selectedProducts.map((product, index) => {
            const variantName = productVariantName(product);
            const orderName = variantName ? `${product.ProductName} (${variantName})` : product.ProductName;
            return `${index + 1}. ${orderName} - Qty: ${product.quantity} - ${currency}${(productPrice(product) * product.quantity).toFixed(2)}`;
        });
        const message = [
            'Hello UKL Butter, I would like to place an order:',
            '',
            ...lines,
            '',
            `Estimated total: ${currency}${orderTotal.toFixed(2)}`,
            'Note: Packing and delivery charges will be added extra.',
            '',
            'Please confirm availability, delivery charges, and payment details.'
        ].join('\n');

        window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            <Helmet>
                <title>{translateText("WhatsApp Order - ")}{Config.SITE_TTILE}</title>
                <meta name="description" content="Choose UKL Butter products and place your order directly through WhatsApp." />
            </Helmet>
            <SiteBreadcrumb title={translateText("WhatsApp Order")} parent="Home" />
            <main className="ukl-whatsapp-order">
                <div className="ukl-whatsapp-order__container">
                    <header className="ukl-whatsapp-order__hero">
                        <span><i className="fa fa-whatsapp" />{translateText(" Quick & personal ordering")}</span>
                        <h1>{translateText("Place your order on WhatsApp")}</h1>
                        <p>{translateText("Choose your products and quantities. We’ll confirm availability, delivery, and payment with you on WhatsApp.")}</p>
                    </header>

                    {loading && <div className="ukl-whatsapp-order__status"><i className="fa fa-spinner fa-spin" />{translateText(" Loading products…")}</div>}
                    {!loading && error && <div className="ukl-whatsapp-order__status ukl-whatsapp-order__status--error">{translateText(error)}</div>}
                    {!loading && !error && products.length === 0 && (
                        <div className="ukl-whatsapp-order__status">{translateText("No products without variants are available right now.")}</div>
                    )}

                    <section className="ukl-whatsapp-products" aria-label={translateText("Products available for WhatsApp order")}>
                        {products.map(product => {
                            const image = asArray(product.ProductImagesJson)[0]?.AttachmentURL;
                            const quantity = Number(quantities[product.ProductId] || 0);
                            const variantName = productVariantName(product);
                            return (
                                <article className={`ukl-whatsapp-product ${quantity > 0 ? 'is-selected' : ''}`} key={product.ProductId}>
                                    <div className="ukl-whatsapp-product__image">
                                        {image ? <img src={Config.ADMIN_BASE_URL + image} alt={translateText(product.ProductName)} /> : <i className="fa fa-shopping-basket" />}
                                    </div>
                                    <div className="ukl-whatsapp-product__body">
                                        <h2>{translateText(product.ProductName)}</h2>
                                        {variantName && (
                                            <span style={{ margin: '-3px 0 8px', color: '#617269', fontSize: '11px', lineHeight: 1.4 }}>
                                                {translateText(variantName)}
                                            </span>
                                        )}
                                        <strong>{currency}{productPrice(product).toFixed(2)}</strong>
                                        <div className="ukl-whatsapp-quantity" aria-label={translateText(`Quantity for ${product.ProductName}`)}>
                                            <button type="button" onClick={() => changeQuantity(product.ProductId, -1)} disabled={quantity === 0} aria-label={translateText("Decrease quantity")}>−</button>
                                            <span>{quantity}</span>
                                            <button type="button" onClick={() => changeQuantity(product.ProductId, 1)} aria-label={translateText("Increase quantity")}>+</button>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </section>

                    <aside className="ukl-whatsapp-order__summary">
                        <div><span>{selectedProducts.length}{translateText(" selected")}</span><strong>{currency}{orderTotal.toFixed(2)}</strong></div>
                        {selectedProducts.length > 0 && !minimumOrderMet && (
                            <p id="whatsapp-minimum-order-error" className="text-danger" role="alert">
                                {translateText(MINIMUM_ORDER_MESSAGE)}. {translateText(MINIMUM_ORDER_GUIDANCE)}
                            </p>
                        )}
                        <button type="button" onClick={placeOrder} disabled={!selectedProducts.length || !minimumOrderMet} aria-describedby={selectedProducts.length > 0 && !minimumOrderMet ? 'whatsapp-minimum-order-error' : undefined}>
                            <i className="fa fa-whatsapp" />{translateText(" WhatsApp Order")}</button>
                        <Link to={`/${langCode}/`}>{translateText("Continue shopping")}</Link>
                    </aside>
                </div>
            </main>
        </>
    );
};

export default WhatsAppOrder;
